import React, { useEffect, useState } from "react";
import * as dayjs from "dayjs";
import { requestAuth } from "../../components/services/RequestService";

const CATEGORIES = {
  LOGISTICA: { label: "Logística", color: "bg-blue-100 text-blue-800" },
  ADUANA: { label: "Aduanas", color: "bg-purple-100 text-purple-800" },
  PAGO: { label: "Finanzas", color: "bg-emerald-100 text-emerald-800" },
  DOCS: { label: "Documentos", color: "bg-orange-100 text-orange-800" },
  ALERTA: { label: "Incidencia", color: "bg-red-100 text-red-800" },
  OTRO: { label: "General", color: "bg-gray-100 text-gray-800" },
};

const formatDate = (dateString) => {
  if (!dateString) return "--/--/----";
  const d = new Date(dateString);
  const userTimezoneOffset = d.getTimezoneOffset() * 60000;
  const localDate = new Date(d.getTime() + userTimezoneOffset);
  return dayjs(localDate).format("DD/MM/YYYY");
};

const parseMeta = (value) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
};

const getEventComments = (meta) =>
  Array.isArray(meta?.comments) ? meta.comments : [];

export const ExpandedBitacora = ({ procedureId, fallbackData = [], fallbackLoading }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchBitacora() {
      if (!procedureId) {
        setEvents([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const fieldsRes = await requestAuth(
          "get",
          `/Procedure/${procedureId}/datasets?fieldnames=historialDeSeguimiento`,
        );
        const fieldId = fieldsRes.data?.find((field) => field.name === "historialDeSeguimiento")?.id;
        let followUpData = [];

        if (fieldId) {
          const followUpRes = await requestAuth("get", `/dataSet/procedure/${procedureId}/field/${fieldId}/subdata`);
          followUpData = followUpRes.data || [];
        }

        if (isMounted) {
          setEvents(followUpData.length > 0 ? followUpData : fallbackData || []);
        }
      } catch {
        if (isMounted) setEvents(fallbackData || []);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchBitacora();
    return () => {
      isMounted = false;
    };
  }, [procedureId, fallbackData]);

  if (!procedureId) {
    return (
      <div className="p-4 text-sm text-slate-400 italic text-center">
        ID no disponible para cargar bitácora.
      </div>
    );
  }

  if (loading || fallbackLoading) {
    return (
      <div className="flex justify-center p-4 text-orange-500">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-orange-200 border-t-orange-500"></span>
      </div>
    );
  }

  const sortedData = [...events].sort((a, b) => {
    const dateA = parseMeta(a.description2)?.eventDate || a.createdAt;
    const dateB = parseMeta(b.description2)?.eventDate || b.createdAt;
    return new Date(dateA) - new Date(dateB);
  });

  if (sortedData.length === 0) {
    return (
      <div className="p-4 text-sm text-slate-400 italic text-center">
        No hay eventos registrados en la bitácora de este embarque.
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wide">Bitácora</div>
        <div className="text-[11px] text-slate-400">
          {sortedData.length} {sortedData.length === 1 ? "evento" : "eventos"}
        </div>
      </div>
      <div className="max-h-[260px] overflow-y-auto divide-y divide-slate-100">
        {sortedData.map((item) => {
          const meta = parseMeta(item.description2);
          const cat = CATEGORIES[meta.category] || CATEGORIES.OTRO;
          const eventDateObj = meta.eventDate ? new Date(meta.eventDate) : new Date(item.createdAt);
          const comments = [...getEventComments(meta)].sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
          );

          return (
            <div key={item.id} className="grid grid-cols-1 md:grid-cols-[92px_108px_1fr] gap-2 px-3 py-2 hover:bg-slate-50 transition-colors">
              <div className="text-xs font-semibold text-slate-600 pt-0.5">{formatDate(eventDateObj)}</div>
              <div>
                <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${cat.color}`}>
                  {cat.label}
                </span>
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{item.label}</div>
                {meta.comment && <div className="text-xs text-slate-600 mt-0.5">{meta.comment}</div>}
                {comments.length > 0 && (
                  <div className="mt-2 space-y-1 border-l-2 border-slate-200 pl-2">
                    {comments.map((comment) => (
                      <div
                        key={comment.id || `${comment.createdAt}-${comment.text}`}
                        className="text-[11px] text-slate-500"
                      >
                        <span className="font-semibold text-slate-600">
                          {comment.createdBy || "Usuario"}:
                        </span>{" "}
                        {comment.text}
                      </div>
                    ))}
                  </div>
                )}
                {meta.location && (
                  <div className="text-[11px] text-slate-400 mt-1 truncate">
                    <span className="mr-1 font-semibold">LOC</span>
                    {meta.location}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
